import { Link } from "react-router-dom";
import Reveal from "@/components/Reveal";
import GoldSeam from "@/components/GoldSeam";

const FACTS = [
  { k: "07+", v: "years building & leading ventures" },
  { k: "03", v: "hospitality & lifestyle brands" },
  { k: "NLP", v: "certified practitioner" },
];

export default function About() {
  return (
    <main className="pt-16" data-testid="about-page">
      <section className="relative py-24 md:py-36 overflow-hidden">
        <div
          className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(169,129,47,0.105), transparent 62%)" }}
          aria-hidden="true"
        />
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          <div>
            <Reveal>
              <p className="eyebrow mb-6">The Founder</p>
              <h1
                className="font-display text-ink font-medium leading-[1.06]"
                style={{ fontSize: "clamp(2.6rem, 5.5vw, 4.6rem)" }}
                data-testid="about-heading"
              >
                Rajdeep Brar
              </h1>
              <p className="mt-4 text-[11px] uppercase tracking-[0.28em] text-gold/80">
                Human Dynamics &amp; Transformation Strategist
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="mt-9 space-y-5 text-inkdim leading-relaxed text-[15px] max-w-lg">
                <p>
                  Rajdeep spent more than seven years in the rooms where human dynamics are
                  impossible to ignore — building hospitality and lifestyle ventures, founding
                  the Alchemy &amp; Earth jewelry brand, and leading large restaurant teams where
                  a single unspoken pattern could shift an entire service.
                </p>
                <p>
                  That decade taught her to read people the way others read spreadsheets:
                  the pattern under the performance, the protection under the reaction,
                  the loop under the language. As an NLP-certified practitioner, she now
                  works with high-achieving, self-aware individuals who are quietly exhausted
                  from running that loop in new clothing.
                </p>
                <p>
                  Her readings are known for one thing: they land. Seekers bring her the
                  question they can't put down — a relationship, a career, a self they're
                  trying to become — and leave able to see the pattern they've been living
                  inside, along with the first honest remedy.
                </p>
                <p className="text-ink">
                  T.A.L.K. — The Analog Life Kintsugi — is where that work lives.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg" data-testid="about-facts">
                {FACTS.map((f) => (
                  <div key={f.k} className="border-t border-[rgba(169,129,47,0.26)] pt-4">
                    <p className="font-display text-3xl text-goldink">{f.k}</p>
                    <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-inkdim leading-relaxed">{f.v}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.12} className="relative lg:pt-10">
            <div className="relative border border-[rgba(169,129,47,0.26)] p-3 md:p-4" data-testid="about-page-image-frame">
              <span className="absolute -top-px -left-px w-5 h-5 border-t border-l border-gold" aria-hidden="true" />
              <span className="absolute -top-px -right-px w-5 h-5 border-t border-r border-gold" aria-hidden="true" />
              <span className="absolute -bottom-px -left-px w-5 h-5 border-b border-l border-gold" aria-hidden="true" />
              <span className="absolute -bottom-px -right-px w-5 h-5 border-b border-r border-gold" aria-hidden="true" />
              <div className="overflow-hidden">
                <img
                  src="/images/rajdeep-portrait.jpg"
                  alt="Rajdeep Brar, founder of T.A.L.K. — The Analog Life Kintsugi"
                  loading="lazy"
                  className="w-full aspect-[4/5] object-cover object-center hover:scale-[1.03] transition-transform duration-[1400ms] ease-out"
                  data-testid="about-page-image"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-paper border-t border-[rgba(169,129,47,0.26)]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <GoldSeam variant="horizontal" className="mx-auto mb-12 w-64 h-10 opacity-60" delay={0.1} />
            <blockquote
              className="font-display italic text-goldink leading-[1.3] text-3xl sm:text-4xl"
              data-testid="about-page-quote"
            >
              "I don't tell people how to live. I help them see the <span className="text-gold">patterns</span> they couldn't see before."
            </blockquote>
            <Link
              to="/contact"
              data-testid="about-cta-button"
              className="inline-block mt-12 bg-deep text-canvas text-xs uppercase tracking-[0.28em] font-semibold px-9 py-4 hover:bg-gold transition-colors duration-300"
            >
              Start a T.A.L.K.
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
